import { unroundedLeaveAccrual } from './leave-accrual-expectation';
import type { NursesPersonalLeaveBasis } from './nurses-personal-leave-basis';

export const LEAVE_SETUP_RULE_VERSION = 'employee-leave-setup-v1';
export const LEAVE_SETUP_DESCRIPTORS = {
  LEAVE_SETUP_FACTS_UNKNOWN: {
    title: 'Leave setup needs employment evidence',
    detail:
      'Confirm effective employment, agreement, leave mappings and payroll calendar before proposing a repair.',
  },
  LEAVE_SETUP_TEMPLATE_UNKNOWN: {
    title: 'Employee leave template could not be checked',
    detail: 'Refresh the employee from Xero before assessing missing leave lines.',
  },
  LEAVE_SETUP_LINE_AMBIGUOUS: {
    title: 'Employee has duplicate leave template lines',
    detail: 'Review duplicate leave lines in Xero before proposing a repair.',
  },
  LEAVE_SETUP_ANNUAL_MISSING: {
    title: 'Permanent employee has no annual leave line',
    detail: 'Add an annual leave template line using the confirmed leave type.',
  },
  LEAVE_SETUP_PERSONAL_MISSING: {
    title: 'Permanent employee has no personal leave line for their agreement',
    detail: 'Add the personal leave template line for the employee’s effective agreement.',
  },
  LEAVE_SETUP_PERSONAL_MANUAL: {
    title: 'Personal leave is set to manual calculation',
    detail: 'Replace manual calculation with the entitlement settings for this employee.',
  },
  LEAVE_SETUP_PERSONAL_SETTINGS: {
    title: 'Personal leave settings differ from the entitlement',
    detail: 'Update personal leave to the desired calculation and annual hours.',
  },
  LEAVE_SETUP_WRONG_AGREEMENT: {
    title: 'Personal leave for the wrong agreement is still accruing',
    detail:
      'Stop accrual on the wrong personal leave line; review its existing balance separately.',
  },
  LEAVE_SETUP_NURSES_FIXED: {
    title: 'Part-time nurse needs fixed personal leave accrual',
    detail: 'Use fixed accrual with 114 or 152 annual hours from the checked three-month average.',
  },
  LEAVE_SETUP_NURSES_BASIS_UNKNOWN: {
    title: 'Nurses personal leave average could not be established',
    detail: 'Check approved timesheets, or engaged hours during the first three months of service.',
  },
  LEAVE_SETUP_PERMANENT_CASUAL_LINE: {
    title: 'Permanent employee is paid on casual earnings lines',
    detail:
      'Use permanent ordinary earnings lines so leave can accrue; review any missing casual loading separately.',
  },
  LEAVE_SETUP_CASUAL_ACCRUING: {
    title: 'Casual employee has accruing paid leave lines',
    detail: 'Stop annual and personal leave accrual on the casual employee’s template.',
  },
  LEAVE_SETUP_MANUAL_RECONCILIATION: {
    title: 'Leave was paid on a manual reconciliation earnings line',
    detail: 'Review the posted payslip for a separate leave balance deduction proposal.',
  },
} as const;
export type LeaveSetupCode = keyof typeof LEAVE_SETUP_DESCRIPTORS;
export interface LeaveSetupSettings {
  CalculationType: 'BASEDONORDINARYEARNINGS' | 'FIXEDAMOUNTEACHPERIOD' | 'NOTCALCULATED';
  AnnualNumberOfUnits?: number;
  NumberOfUnits?: number;
  FullTimeNumberOfUnitsPerPeriod?: number;
}
export interface LeaveSetupFinding {
  code: LeaveSetupCode;
  leaveTypeId: string | null;
  desired: {
    type: 'TEMPLATE_LINE_ADD' | 'TEMPLATE_LINE_UPDATE';
    settings: LeaveSetupSettings;
  } | null;
  /** Policy-eligible finding only; payslip writes warn unless the tenant explicitly opts in. */
  refusesPayslipWrite: boolean;
}
export interface EmployeeLeaveSetupFacts {
  agreement: 'GS' | 'NURSES' | 'UNKNOWN';
  employment: 'PERMANENT_FT' | 'PERMANENT_PT' | 'CASUAL' | 'UNKNOWN';
  leaveTypeIds: { annual: string | null; personalGS: string | null; personalNurses: string | null };
  templateLines: readonly Record<string, unknown>[] | null;
  periodsPerYear: number | null;
  nursesBasis?: NursesPersonalLeaveBasis;
  earningsLines?: readonly { name: string; units: number | null; ratePerUnit?: number | null }[];
}
export function isCasualEarningsLabel(name: string): boolean {
  return /\bcasual\b/i.test(name) && /ordinary\s+time|saturday|sunday/i.test(name);
}
function calculation(line: Record<string, unknown>): string {
  return String(line.CalculationType ?? '')
    .toUpperCase()
    .replace(/[\s_-]/g, '');
}
function accruing(line: Record<string, unknown>): boolean {
  return ['BASEDONORDINARYEARNINGS', 'FIXEDAMOUNTEACHPERIOD'].includes(calculation(line));
}
function manual(line: Record<string, unknown>): boolean {
  return ['3', 'MANUAL', 'NOCALCULATION', 'NOTCALCULATED'].includes(calculation(line));
}
/** Pure observation and absolute settings only. Never infers a balance delta. */
export function evaluateEmployeeLeaveSetup(facts: EmployeeLeaveSetupFacts): LeaveSetupFinding[] {
  const findings: LeaveSetupFinding[] = [];
  const add = (
    code: LeaveSetupCode,
    leaveTypeId: string | null = null,
    desired: LeaveSetupFinding['desired'] = null,
    refusesPayslipWrite = false,
  ) => findings.push({ code, leaveTypeId, desired, refusesPayslipWrite });
  for (const line of facts.earningsLines ?? []) {
    if (line.units === null || line.units === 0) continue;
    if (/leave.*manual[\s–—-]*reconciliation/i.test(line.name))
      add('LEAVE_SETUP_MANUAL_RECONCILIATION');
    if (
      ['PERMANENT_FT', 'PERMANENT_PT'].includes(facts.employment) &&
      isCasualEarningsLabel(line.name)
    )
      add('LEAVE_SETUP_PERMANENT_CASUAL_LINE', null, null, true);
  }
  if (facts.templateLines === null) {
    add('LEAVE_SETUP_TEMPLATE_UNKNOWN');
    return findings;
  }
  const ids = Object.values(facts.leaveTypeIds).filter((id): id is string => Boolean(id));
  if (
    facts.agreement === 'UNKNOWN' ||
    facts.employment === 'UNKNOWN' ||
    !facts.leaveTypeIds.annual ||
    !(facts.agreement === 'GS'
      ? facts.leaveTypeIds.personalGS
      : facts.leaveTypeIds.personalNurses) ||
    new Set(ids).size !== ids.length ||
    !facts.periodsPerYear
  ) {
    add('LEAVE_SETUP_FACTS_UNKNOWN');
    return findings;
  }
  const lines = facts.templateLines;
  if (ids.some((id) => lines.filter((line) => line.LeaveTypeID === id).length > 1)) {
    add('LEAVE_SETUP_LINE_AMBIGUOUS');
    return findings;
  }
  const disable = { CalculationType: 'NOTCALCULATED' } as const;
  if (facts.employment === 'CASUAL') {
    for (const line of lines)
      if (ids.includes(String(line.LeaveTypeID)) && accruing(line))
        add('LEAVE_SETUP_CASUAL_ACCRUING', String(line.LeaveTypeID), {
          type: 'TEMPLATE_LINE_UPDATE',
          settings: disable,
        });
    return findings;
  }
  const annualId = facts.leaveTypeIds.annual!;
  if (!lines.some((line) => line.LeaveTypeID === annualId))
    add(
      'LEAVE_SETUP_ANNUAL_MISSING',
      annualId,
      {
        type: 'TEMPLATE_LINE_ADD',
        settings: {
          CalculationType: 'BASEDONORDINARYEARNINGS',
          AnnualNumberOfUnits: 152,
          FullTimeNumberOfUnitsPerPeriod: 1976 / facts.periodsPerYear,
        },
      },
      true,
    );
  const personalId = (
    facts.agreement === 'GS' ? facts.leaveTypeIds.personalGS : facts.leaveTypeIds.personalNurses
  )!;
  const wrongId = (
    facts.agreement === 'GS' ? facts.leaveTypeIds.personalNurses : facts.leaveTypeIds.personalGS
  )!;
  const wrong = lines.find((line) => line.LeaveTypeID === wrongId);
  if (wrong && accruing(wrong))
    add('LEAVE_SETUP_WRONG_AGREEMENT', wrongId, {
      type: 'TEMPLATE_LINE_UPDATE',
      settings: disable,
    });
  const personal = lines.find((line) => line.LeaveTypeID === personalId);
  const expectation = unroundedLeaveAccrual({
    agreement: facts.agreement,
    employmentType: facts.employment,
    workerCategory: 'DAY',
    leaveType: 'PERSONAL',
    serviceHoursUnits1e4: 0,
    nursesAverageWeeklyHours: facts.nursesBasis?.averageWeeklyHours ?? undefined,
    periodsPerYear: facts.periodsPerYear,
  });
  if (expectation.annualRateUnits1e4 === null) {
    if (!personal) add('LEAVE_SETUP_PERSONAL_MISSING', personalId, null, true);
    else if (manual(personal)) add('LEAVE_SETUP_PERSONAL_MANUAL', personalId);
    add('LEAVE_SETUP_NURSES_BASIS_UNKNOWN', personalId);
    return findings;
  }
  // eslint-disable-next-line statura/no-floating-point-money -- integer leave hours × 10000 converted to absolute Xero wire hours, not currency
  const annual = expectation.annualRateUnits1e4 / 10000;
  const fixed = expectation.calculationType === 'FIXED_PER_PERIOD';
  const settings: LeaveSetupSettings = fixed
    ? {
        CalculationType: 'FIXEDAMOUNTEACHPERIOD',
        AnnualNumberOfUnits: annual,
        NumberOfUnits: annual,
      }
    : {
        CalculationType: 'BASEDONORDINARYEARNINGS',
        AnnualNumberOfUnits: annual,
        FullTimeNumberOfUnitsPerPeriod: 1976 / facts.periodsPerYear,
      };
  if (!personal)
    add('LEAVE_SETUP_PERSONAL_MISSING', personalId, { type: 'TEMPLATE_LINE_ADD', settings }, true);
  else if (Object.entries(settings).some(([key, value]) => personal[key] !== value))
    add(
      manual(personal)
        ? 'LEAVE_SETUP_PERSONAL_MANUAL'
        : fixed
          ? 'LEAVE_SETUP_NURSES_FIXED'
          : 'LEAVE_SETUP_PERSONAL_SETTINGS',
      personalId,
      { type: 'TEMPLATE_LINE_UPDATE', settings },
    );
  return findings.filter(
    (finding, i) =>
      findings.findIndex(
        (other) => other.code === finding.code && other.leaveTypeId === finding.leaveTypeId,
      ) === i,
  );
}
